import React from "react";
import PropTypes from "prop-types";
import { withStyles } from "@mui/styles";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";

const styles = () => ({
  tablePaper: {
    backgroundColor: "#1C1E20",
    border: "1px solid rgba(255, 255, 255, 0.08)",
    borderRadius: "12px",
    overflow: "hidden",
  },
  table: {
    minWidth: 720,
  },
  headCell: {
    color: "rgba(255,255,255,0.7)",
    fontWeight: 700,
    borderBottom: "1px solid rgba(255,255,255,0.08)",
    backgroundColor: "rgba(255,255,255,0.02)",
  },
  bodyCell: {
    color: "#ffffff",
    borderBottom: "1px solid rgba(255,255,255,0.06)",
  },
  secondaryText: {
    color: "rgba(255,255,255,0.65)",
    fontSize: "0.85rem",
  },
  positive: {
    color: "#00C853",
    fontWeight: 700,
  },
  negative: {
    color: "#F44336",
    fontWeight: 700,
  },
  neutral: {
    color: "rgba(255,255,255,0.85)",
    fontWeight: 700,
  },
  statusChip: {
    fontWeight: 700,
    height: "24px",
  },
  emptyState: {
    padding: "1.5rem",
    color: "rgba(255,255,255,0.7)",
  },
});

function formatSignedNumber(value, digits = 0) {
  if (value == null || Number.isNaN(Number(value))) return "-";
  const numericValue = Number(value);
  const fixed = digits > 0 ? numericValue.toFixed(digits) : String(Math.round(numericValue));
  return numericValue > 0 ? `+${fixed}` : fixed;
}

function formatTimestamp(timestamp) {
  const numericTimestamp = Number(timestamp);
  if (!Number.isFinite(numericTimestamp)) return "-";
  return new Date(numericTimestamp).toLocaleString();
}

function getValueClass(classes, value) {
  if (value > 0) return classes.positive;
  if (value < 0) return classes.negative;
  return classes.neutral;
}

function buildStatusChipProps(status) {
  if (status === "applied" || status === "already_applied") {
    return { label: "Applied", sx: { backgroundColor: "rgba(0, 200, 83, 0.16)", color: "#00C853" } };
  }
  if (status === "partial_pending") {
    return { label: "Partial", sx: { backgroundColor: "rgba(255, 193, 7, 0.16)", color: "#FFC107" } };
  }
  if (status === "missing_user") {
    return { label: "Missing user", sx: { backgroundColor: "rgba(255, 152, 0, 0.16)", color: "#FF9800" } };
  }
  if (status === "failed") {
    return { label: "Failed", sx: { backgroundColor: "rgba(244, 67, 54, 0.16)", color: "#F44336" } };
  }
  return { label: "Pending", sx: { backgroundColor: "rgba(255,255,255,0.08)", color: "#ffffff" } };
}

function MatchOutcomeHistoryTable({ classes, rows }) {
  if (!Array.isArray(rows) || rows.length === 0) {
    return (
      <Paper className={classes.tablePaper} elevation={0}>
        <Typography className={classes.emptyState}>
          No private match outcome deltas are available yet.
        </Typography>
      </Paper>
    );
  }

  return (
    <TableContainer component={Paper} className={classes.tablePaper} elevation={0}>
      <Table className={classes.table}>
        <TableHead>
          <TableRow>
            <TableCell className={classes.headCell}>Match</TableCell>
            <TableCell className={classes.headCell}>Result</TableCell>
            <TableCell className={classes.headCell}>ELO</TableCell>
            <TableCell className={classes.headCell}>Rating 3.0</TableCell>
            <TableCell className={classes.headCell}>Reputation</TableCell>
            <TableCell className={classes.headCell}>Status</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => {
            const status = row.reputation?.status || row.rating3?.status || "pending";
            const chipProps = buildStatusChipProps(status);
            const rating3Delta =
              row.rating3?.before != null && row.rating3?.after != null
                ? Number((row.rating3.after - row.rating3.before).toFixed(2))
                : null;

            return (
              <TableRow key={row.matchId}>
                <TableCell className={classes.bodyCell}>
                  <Typography>{row.map || "Unknown map"}</Typography>
                  <Typography className={classes.secondaryText}>{formatTimestamp(row.timestamp)}</Typography>
                </TableCell>
                <TableCell className={classes.bodyCell}>{row.outcome || "-"}</TableCell>
                <TableCell className={classes.bodyCell}>
                  <Typography className={getValueClass(classes, row.elo?.change || 0)}>
                    {formatSignedNumber(row.elo?.change, 0)}
                  </Typography>
                  <Typography className={classes.secondaryText}>
                    {row.elo?.before != null && row.elo?.after != null
                      ? `${row.elo.before} -> ${row.elo.after}`
                      : "-"}
                  </Typography>
                </TableCell>
                <TableCell className={classes.bodyCell}>
                  <Typography className={getValueClass(classes, rating3Delta || 0)}>
                    {row.rating3?.matchRating != null ? row.rating3.matchRating.toFixed(2) : "-"}
                  </Typography>
                  <Typography className={classes.secondaryText}>
                    {row.rating3?.before != null && row.rating3?.after != null
                      ? `${row.rating3.before.toFixed(2)} -> ${row.rating3.after.toFixed(2)} (${formatSignedNumber(
                          rating3Delta,
                          2
                        )})`
                      : row.rating3?.status === "missing_user"
                        ? "User not found"
                        : "-"}
                  </Typography>
                </TableCell>
                <TableCell className={classes.bodyCell}>
                  <Typography className={getValueClass(classes, row.reputation?.delta || 0)}>
                    {formatSignedNumber(row.reputation?.delta, 0)}
                  </Typography>
                  <Typography className={classes.secondaryText}>
                    {row.reputation?.before != null && row.reputation?.after != null
                      ? `${row.reputation.before} -> ${row.reputation.after}`
                      : "-"}
                  </Typography>
                </TableCell>
                <TableCell className={classes.bodyCell}>
                  <Chip
                    label={chipProps.label}
                    className={classes.statusChip}
                    sx={chipProps.sx}
                    size="small"
                  />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

MatchOutcomeHistoryTable.propTypes = {
  classes: PropTypes.object.isRequired,
  rows: PropTypes.array,
};

MatchOutcomeHistoryTable.defaultProps = {
  rows: [],
};

export default withStyles(styles)(MatchOutcomeHistoryTable);
